import React from 'react';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography/index.js';
import { BREAKPOINTS } from 'constants/grid';
import { formatAsPositivePoundsOrPence } from 'utils/currency';
import GoToTrolley from 'components/GoToTrolley';
import { useMinSpend } from 'hooks/use-min-spend';
import TrolleyButtonContents from './MobileTrolleyButtonContents';
import styles from './MobileShoppingSummary.module.scss';

type MobileShoppingSummaryProps = {
  loadingStatus?: boolean;
  totalEstimatedCost?: number;
  totalNumberOfItems?: number;
  hasTrolleyError?: boolean;
  testIdSuffix?: string;
  scrolled?: boolean;
};

export const MobileShoppingSummary = ({
  loadingStatus = false,
  totalEstimatedCost = 0,
  totalNumberOfItems = 0,
  hasTrolleyError = false,
  testIdSuffix = '',
  scrolled,
}: MobileShoppingSummaryProps) => {
  const estimatedCost = formatAsPositivePoundsOrPence(totalEstimatedCost);
  const description = `trolley, ${totalNumberOfItems} item${
    totalNumberOfItems !== 1 ? 's' : ''
  } ${estimatedCost} total, view trolley.`;

  const { showMinSpend, minSpend } = useMinSpend();
  const isMobileBreakpoint = useMediaQuery({ maxWidth: BREAKPOINTS.md });

  return (
    <div className={styles.shoppingSummary}>
      <section
        className={styles.trolleyActions}
        data-actiontype="redirect"
        data-customdata="category:Header"
        data-origincomponent="ShoppingSummary"
        data-shortdescription={hasTrolleyError ? null : description}
      >
        {/* TODO Have GoToTrolley & TrolleyButtonContents components as JSX here if not reused */}
        <GoToTrolley
          className={classNames(styles.trolleyLink)}
          originComponent="site-header accessible trolley link"
          testIdSuffix={testIdSuffix}
          title={hasTrolleyError ? null : description}
        >
          <TrolleyButtonContents
            hasTrolleyError={hasTrolleyError}
            estimatedCost={estimatedCost}
            loadingStatus={loadingStatus}
            totalNumberOfItems={totalNumberOfItems}
            testIdSuffix={testIdSuffix}
          />
        </GoToTrolley>
        <div
          className={classNames(styles.minSpendWrap, {
            [styles.show]: showMinSpend,
            [styles.hide]: !showMinSpend,
            [styles.scrolled]: scrolled,
          })}
        >
          <Typography styleAs={!isMobileBreakpoint ? 'paragraphSmall' : 'paragraphExtraSmall'}>
            {!isMobileBreakpoint ? 'Minimum' : 'Min.'}{' '}
            <span className="sr-only">{isMobileBreakpoint ? 'Min.' : ''} order</span> £{minSpend}
          </Typography>
        </div>
      </section>
    </div>
  );
};
